import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { AddProjectModal } from '../components/domain/AddProjectModal.js';
import { UploadModal } from '../components/domain/UploadModal.js';
import { AddDocModal } from '../components/domain/AddDocModal.js';
import { DocViewerModal } from '../components/domain/DocViewerModal.js';
import { downloadCSV } from '../lib/csv.js';
import { useResetSub } from '../features/filters/useFilters.js';
import { SearchableSelect } from '../components/primitives/SearchableSelect.js';
import { WaIcon } from '../components/primitives/WaIcon.js';
import { RealMap, positionTooltip, type RealMapPin } from '../components/primitives/RealMap.js';
import { waLink } from '../lib/wa.js';

interface ProjectDoc {
  id: string;
  name: string;
  type?: string;
  url?: string | null;
  date?: string | null;
  ref?: string | null;
  notes?: string | null;
  removed?: boolean;
  removedAt?: string | null;
  fileName?: string | null;
}

interface Project {
  id: string;
  code: string;
  name: string;
  status: 'live' | 'completed' | 'tender' | 'cancelled' | 'upcoming';
  customerId: string | null;
  customerRef?: { id: string; name: string } | null;
  customer?: string | null;
  custRef?: string | null;
  customerRefNo?: string | null;
  manager: string | null;
  managerPhone?: string | null;
  managerEmail?: string | null;
  supervisorId?: string | null;
  supervisorName?: string | null;
  supervisorPhone?: string | null;
  supervisorEmail?: string | null;
  clientPMId?: string | null;
  clientFMId?: string | null;
  clientCMId?: string | null;
  clsMgrId?: string | null;
  postcode: string | null;
  address: string | null;
  startDate: string | null;
  endDate: string | null;
  lat: number | null;
  lng: number | null;
  documents?: ProjectDoc[];
  clientContacts?: string[];
}

interface Worker {
  id: string;
  name: string;
  role: string;
  roles?: string[];
  status: string;
  phone?: string;
  photo?: string | null;
}

interface Deployment {
  id: string;
  workerId: string;
  workerName?: string;
  projectId: string;
  status: string;
  startDate: string;
  endDate?: string | null;
}

interface SignInRecord {
  id: string;
  workerId: string;
  workerName?: string;
  workerRole?: string;
  projectId: string | null;
  type: 'in' | 'out';
  timestamp: string;
  withinGeofence?: boolean | null;
}

interface SharedDocRecipient {
  id: string;
  workerId: string | null;
  workerName: string;
  status: string;
  signedAt: string | null;
  signatureImageUrl?: string | null;
  confirmedName?: string | null;
}

interface SharedDoc {
  id: string;
  projectId: string | null;
  name: string;
  docType?: string | null;
  createdAt: string;
  createdByName?: string | null;
  recipients: SharedDocRecipient[];
}

interface Contact {
  id: string;
  name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  customer: string | null;
  customerId: string | null;
}

type SortCol = 'id' | 'name' | 'customer' | 'manager' | 'end' | null;
type SortDir = 1 | -1;
type PTab = 'details' | 'docs' | 'signin' | 'sigs';

function bdg(status: string): JSX.Element {
  return <span className={`badge b-${status}`}>{status}</span>;
}

function initials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

function avEl(w: { name: string; photo?: string | null }, size = 26, font = 10): JSX.Element {
  if (w.photo) {
    return (
      <div style={{ width: size, height: size, minWidth: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
        <img src={w.photo} alt={w.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }
  let h = 0;
  for (let i = 0; i < w.name.length; i++) h = (h * 31 + w.name.charCodeAt(i)) >>> 0;
  return (
    <div style={{ width: size, height: size, minWidth: size, borderRadius: '50%', background: `hsl(${h % 360}, 60%, 55%)`, color: '#0B0F1A', fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: font, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {initials(w.name)}
    </div>
  );
}

function ThHdr({ label, col, current, dir, onClick }: { label: string; col: Exclude<SortCol, null>; current: SortCol; dir: SortDir; onClick: (c: Exclude<SortCol, null>) => void }) {
  const arrow = current === col ? (dir === 1 ? ' ▲' : ' ▼') : '';
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => onClick(col)}>
      {label}{arrow}
    </th>
  );
}

const DOC_ICONS: Record<string, string> = {
  Drawings: '📐',
  'Method Statement': '📋',
  'Risk Assessment': '⚠',
  Programme: '📅',
  Contract: '📜',
  'H&S Plan': '🦺',
  Insurance: '🔐',
  Correspondence: '✉',
  Photo: '📷',
  Other: '📄',
};

export function Projects() {
  const { canImport, canExport, canEditProjects: canAdd } = useRolePerms();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'all' | 'live' | 'completed'>('all');
  const [search, setSearch] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fCust, setFCust] = useState('');
  const [fMgr, setFMgr] = useState('');
  const [sortCol, setSortCol] = useState<SortCol>(null);
  const [sortDir, setSortDir] = useState<SortDir>(1);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [pTab, setPTab] = useState<PTab>('details');
  const [docAddFor, setDocAddFor] = useState<Project | null>(null);
  const [docViewer, setDocViewer] = useState<{ url: string; name: string } | null>(null);
  const [siSearch, setSiSearch] = useState('');
  const [siType, setSiType] = useState('');
  const [sigSearch, setSigSearch] = useState('');
  const [sigFilter, setSigFilter] = useState('');
  const mapBodyRef = useRef<HTMLDivElement>(null);
  const [hoverPin, setHoverPin] = useState<{ id: string; leftPx: number; topPx: number } | null>(null);

  useResetSub(() => { setTab('all'); setSearch(''); setFStatus(''); setFCust(''); setFMgr(''); setSortCol(null); setPTab('details'); });

  const [searchParams, setSearchParams] = useSearchParams();

  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<Project[]>('/projects').then((r) => r.data) });
  const { data: workers = [] } = useQuery({ queryKey: ['workers'], queryFn: () => api.get<Worker[]>('/workers').then((r) => r.data) });
  const { data: deployments = [] } = useQuery({ queryKey: ['deployments'], queryFn: () => api.get<Deployment[]>('/deployments').then((r) => r.data) });
  const { data: signinRecords = [] } = useQuery({ queryKey: ['signin-records'], queryFn: () => api.get<SignInRecord[]>('/signin-records').then((r) => r.data) });
  const { data: sharedDocs = [] } = useQuery({ queryKey: ['shared-docs'], queryFn: () => api.get<SharedDoc[]>('/shared-docs').then((r) => r.data) });
  const { data: contacts = [] } = useQuery({ queryKey: ['contacts'], queryFn: () => api.get<Contact[]>('/contacts').then((r) => r.data) });

  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || projects.length === 0) return;
    if (projects.some((p) => p.id === openId)) {
      setActiveId(openId);
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, projects, setSearchParams]);

  const customerOptions = useMemo(
    () => [...new Set(projects.map((p) => p.customerRef?.name).filter(Boolean) as string[])].sort(),
    [projects],
  );
  const managerOptions = useMemo(
    () => [...new Set(projects.map((p) => p.manager).filter(Boolean) as string[])].sort(),
    [projects],
  );

  const rows = useMemo(() => {
    let r = tab === 'all' ? [...projects] : projects.filter((p) => p.status === tab);
    const s = search.toLowerCase().trim();
    if (s) {
      r = r.filter((p) =>
        [p.code, p.name, p.customerRef?.name, p.manager, p.postcode].filter(Boolean).join(' ').toLowerCase().includes(s),
      );
    }
    if (fStatus) r = r.filter((p) => p.status === fStatus);
    if (fCust) r = r.filter((p) => p.customerRef?.name === fCust);
    if (fMgr) r = r.filter((p) => p.manager === fMgr);
    if (sortCol) {
      r = [...r].sort((a, b) => {
        const get = (p: Project): string => {
          switch (sortCol) {
            case 'id': return p.code;
            case 'name': return p.name;
            case 'customer': return p.customerRef?.name ?? '';
            case 'manager': return p.manager ?? '';
            case 'end': return p.endDate ?? '';
            default: return '';
          }
        };
        return get(a).localeCompare(get(b)) * sortDir;
      });
    }
    return r;
  }, [projects, tab, search, fStatus, fCust, fMgr, sortCol, sortDir]);

  const sub = `${projects.length} projects · ${projects.filter((p) => p.status === 'live').length} live`;

  // Map pins for the real Leaflet map
  const mapPins = projects.filter((p): p is Project & { lat: number; lng: number } => p.lat != null && p.lng != null);
  const realMapPins: RealMapPin[] = mapPins.map((p) => ({
    id: p.id,
    lat: p.lat,
    lng: p.lng,
    iconHtml: `<div class="mpin-${p.status === 'live' ? 'live' : 'completed'}" style="display:flex;flex-direction:column;align-items:center"><div class="mpin-dot"></div><div class="mpin-stem"></div></div>`,
    iconSize: [18, 24],
    iconAnchor: [9, 24],
  }));
  const hoverProject = hoverPin ? mapPins.find((x) => x.id === hoverPin.id) : null;

  const active = activeId ? projects.find((p) => p.id === activeId) : null;
  const activeWorkers = active
    ? workers.filter((w) => deployments.some((d) => d.workerId === w.id && d.projectId === active.id && d.status === 'active'))
    : [];
  const activeDocs = active?.documents ?? [];
  const activeDocsLive = activeDocs.filter((d) => !d.removed);
  const activeDocsVoided = activeDocs.filter((d) => d.removed);
  const activeRecentDeps = active
    ? deployments.filter((d) => d.projectId === active.id).slice(0, 4)
    : [];
  const activeContacts = active
    ? contacts.filter((c) => active.clientContacts?.includes(c.id))
    : [];

  const handleSort = (col: Exclude<SortCol, null>) => {
    if (sortCol === col) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortCol(col); setSortDir(1); }
  };

  const clearFilters = () => { setSearch(''); setFStatus(''); setFCust(''); setFMgr(''); };

  // Sign In tab data
  const projSignins = active ? signinRecords.filter((r) => r.projectId === active.id) : [];
  const filteredSignins = projSignins.filter((r) => {
    if (siType && r.type !== siType) return false;
    if (siSearch) {
      const hay = [r.workerName ?? '', r.type, new Date(r.timestamp).toLocaleDateString('en-GB')].join(' ').toLowerCase();
      if (!hay.includes(siSearch.toLowerCase())) return false;
    }
    return true;
  });

  // Sigs tab data
  const projSigs = useMemo(() => {
    if (!active) return [];
    const out: Array<{
      sdId: string; rcpId: string; docName: string; docType: string;
      workerId: string | null; workerName: string; workerRole: string;
      sentBy: string | null; sentAt: string;
      signed: boolean; signedAt: string | null;
      sigData: string | null; nameConfirmed: string;
    }> = [];
    sharedDocs
      .filter((sd) => sd.projectId === active.id)
      .forEach((sd) => {
        sd.recipients.forEach((rcp) => {
          const w = rcp.workerId ? workers.find((x) => x.id === rcp.workerId) : undefined;
          out.push({
            sdId: sd.id,
            rcpId: rcp.id,
            docName: sd.name,
            docType: sd.docType ?? '',
            workerId: rcp.workerId,
            workerName: w?.name ?? rcp.workerName,
            workerRole: w?.role ?? '',
            sentBy: sd.createdByName ?? null,
            sentAt: sd.createdAt,
            signed: rcp.status === 'signed',
            signedAt: rcp.signedAt,
            sigData: rcp.signatureImageUrl ?? null,
            nameConfirmed: rcp.confirmedName ?? '',
          });
        });
      });
    return out;
  }, [active, sharedDocs, workers]);
  const filteredSigs = projSigs.filter((s) => {
    if (sigFilter === 'signed' && !s.signed) return false;
    if (sigFilter === 'pending' && s.signed) return false;
    if (sigSearch) {
      const hay = [s.docName, s.workerName, s.workerRole, s.sentBy ?? ''].join(' ').toLowerCase();
      if (!hay.includes(sigSearch.toLowerCase())) return false;
    }
    return true;
  });
  const sigsSigned = projSigs.filter((s) => s.signed).length;
  const sigsPending = projSigs.filter((s) => !s.signed).length;

  const closeDp = () => { setActiveId(null); setPTab('details'); };

  return (
    <div className="page" id="page-projects">
      <div className="proj-layout" style={{ display: 'flex', gap: 16, height: 'calc(100vh - 150px)' }}>
        <div className="proj-content" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div className="hbar">
            <div>
              <div className="ph">Projects</div>
              <div className="ps" id="proj-sub">{sub}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {canImport ? <button className="btn btn-ghost btn-sm" onClick={() => setImportOpen(true)}>⬆ Import</button> : null}
              {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => {
                const lookup = (id: string | null | undefined) => id ? contacts.find((c) => c.id === id) : null;
                downloadCSV(
                  'projects.csv',
                  rows.map((p) => {
                    const pm = lookup(p.clientPMId);
                    const fm = lookup(p.clientFMId);
                    const cm = lookup(p.clientCMId);
                    const cls = lookup(p.clsMgrId);
                    return {
                      ...p,
                      customerName: p.customerRef?.name ?? '',
                      clientPMName: pm?.name ?? '',
                      clientPMPhone: pm?.phone ?? '',
                      clientFMName: fm?.name ?? '',
                      clientFMPhone: fm?.phone ?? '',
                      clientCMName: cm?.name ?? '',
                      clientCMPhone: cm?.phone ?? '',
                      clsMgrName: cls?.name ?? '',
                      clsMgrPhone: cls?.phone ?? '',
                    };
                  }) as Array<Record<string, unknown>>,
                  [
                    { key: 'code', label: 'Code' },
                    { key: 'name', label: 'Name' },
                    { key: 'status', label: 'Status' },
                    { key: 'manager', label: 'Manager' },
                    { key: 'managerPhone', label: 'Manager Phone' },
                    { key: 'clientPMName', label: 'Client PM' },
                    { key: 'clientPMPhone', label: 'Client PM Phone' },
                    { key: 'clientFMName', label: 'Client FM' },
                    { key: 'clientFMPhone', label: 'Client FM Phone' },
                    { key: 'clientCMName', label: 'Client CM' },
                    { key: 'clientCMPhone', label: 'Client CM Phone' },
                    { key: 'clsMgrName', label: 'CLS Manager' },
                    { key: 'clsMgrPhone', label: 'CLS Manager Phone' },
                    { key: 'customerName', label: 'Customer' },
                    { key: 'startDate', label: 'Start' },
                    { key: 'endDate', label: 'End' },
                    { key: 'address', label: 'Address' },
                    { key: 'postcode', label: 'Postcode' },
                  ]
                );
              }}>⬇ Export</button> : null}
              {canAdd ? <button className="btn btn-primary" onClick={() => setAddOpen(true)}>+ New Project</button> : null}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 8, padding: '5px 8px', background: 'var(--card)', borderRadius: 8, border: '1px solid var(--border)', minHeight: 36 }}>
            <div className="tbl-search" style={{ minWidth: 180, flex: 1, margin: 0 }}>
              <span style={{ color: 'var(--faint)' }}>⌕</span>
              <input id="proj-search" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="snav-sep"></div>
            <div className="fpill">
              <span>Status:</span>
              <SearchableSelect
                id="proj-f-status"
                value={fStatus}
                onChange={setFStatus}
                placeholder="All"
                options={[
                  { value: '', label: 'All' },
                  { value: 'live', label: 'Live' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'tender', label: 'Tender' },
                  { value: 'cancelled', label: 'Cancelled' },
                ]}
              />
            </div>
            <div className="fpill">
              <span>Customer:</span>
              <SearchableSelect
                id="proj-f-customer"
                value={fCust}
                onChange={setFCust}
                placeholder="All"
                options={[{ value: '', label: 'All' }, ...customerOptions.map((c) => ({ value: c, label: c }))]}
              />
            </div>
            <div className="fpill">
              <span>Manager:</span>
              <SearchableSelect
                id="proj-f-manager"
                value={fMgr}
                onChange={setFMgr}
                placeholder="All"
                options={[{ value: '', label: 'All' }, ...managerOptions.map((m) => ({ value: m, label: m }))]}
              />
            </div>
            <div className="snav-sep"></div>
            <span id="proj-count" style={{ fontSize: 10, color: 'var(--muted)' }}>
              {rows.length} project{rows.length !== 1 ? 's' : ''} shown
            </span>
            <button className="btn btn-ghost btn-xs" onClick={clearFilters} title="Clear filters" style={{ marginLeft: 'auto' }}>✕ Clear</button>
          </div>
          <div className="tab-bar" id="proj-tabs">
            {(['all', 'live', 'completed'] as const).map((f) => (
              <div key={f} className={`tab ${tab === f ? 'active' : ''}`} onClick={() => setTab(f)}>{f}</div>
            ))}
          </div>
          <div className="card proj-table-card" style={{ flex: 1, overflow: 'auto' }}>
            <table>
              <thead id="proj-thead">
                <tr>
                  <ThHdr label="Code" col="id" current={sortCol} dir={sortDir} onClick={handleSort} />
                  <ThHdr label="Project" col="name" current={sortCol} dir={sortDir} onClick={handleSort} />
                  <ThHdr label="Customer" col="customer" current={sortCol} dir={sortDir} onClick={handleSort} />
                  <th>Status</th>
                  <ThHdr label="Manager" col="manager" current={sortCol} dir={sortDir} onClick={handleSort} />
                  <th>Supervisor</th>
                  <th>Workers</th>
                  <ThHdr label="End" col="end" current={sortCol} dir={sortDir} onClick={handleSort} />
                </tr>
              </thead>
              <tbody id="proj-tbody">
                {rows.map((p) => {
                  const wc = workers.filter((w) => deployments.some((d) => d.workerId === w.id && d.projectId === p.id && d.status === 'active')).length;
                  const dc = (p.documents || []).filter((d) => !d.removed).length;
                  return (
                    <tr
                      key={p.id}
                      onClick={() => setActiveId(p.id)}
                      style={{ cursor: 'pointer', background: activeId === p.id ? 'rgba(245,158,11,.12)' : undefined, boxShadow: activeId === p.id ? 'inset 3px 0 0 var(--accent)' : undefined }}
                    >
                      <td><span style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 11 }}>{p.code}</span></td>
                      <td style={{ fontWeight: 500 }}>{p.name}</td>
                      <td style={{ color: 'var(--muted)' }}>{p.customerRef?.name ?? ''}</td>
                      <td>{bdg(p.status)}</td>
                      <td>{p.manager ?? ''}</td>
                      <td style={{ fontSize: 11 }}>
                        {p.supervisorName ? <span style={{ fontWeight: 500 }}>{p.supervisorName}</span> : <span style={{ color: 'var(--faint)' }}>—</span>}
                      </td>
                      <td>
                        <span style={{ color: 'var(--live)', fontWeight: 600 }}>{wc}</span>
                        {dc ? <span style={{ marginLeft: 6, color: 'var(--muted)', fontSize: 10 }}>📄{dc}</span> : null}
                      </td>
                      <td style={{ color: 'var(--muted)' }}>{p.endDate?.slice(0, 10) ?? ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="map-wrap" id="proj-map" style={{ marginTop: 10, flexShrink: 0 }}>
            <div className="map-hdr">
              <span style={{ fontFamily: 'Syne, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--muted)' }}>Project Locations</span>
              <div style={{ display: 'flex', gap: 10, fontSize: 10, color: 'var(--muted)', alignItems: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span className="dot dot-live"></span>Live</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span className="dot dot-completed"></span>Completed</span>
              </div>
            </div>
            <div className="map-body" ref={mapBodyRef} style={{ height: 200 }}>
              {mapPins.length === 0 ? (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 11, zIndex: 2 }}>
                  No projects with coordinates
                </div>
              ) : null}
              <RealMap
                pins={realMapPins}
                height="100%"
                onPinEnter={(id, cp) => {
                  const pos = positionTooltip(cp, mapBodyRef.current);
                  setHoverPin({ id, leftPx: pos.leftPx, topPx: pos.topPx });
                }}
                onPinLeave={() => setHoverPin(null)}
                onPinClick={(id) => setActiveId(id)}
              >
                {hoverPin && hoverProject ? (
                  <div className="map-tt on" style={{ left: hoverPin.leftPx, top: hoverPin.topPx }}>
                    <div className="map-tt-title">{hoverProject.code}</div>
                    <div style={{ fontWeight: 500, marginBottom: 2, fontSize: 12 }}>{hoverProject.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4 }}>{hoverProject.address ?? ''}</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <span className={`badge b-${hoverProject.status === 'live' ? 'live' : 'completed'}`}>{hoverProject.status}</span>
                    </div>
                  </div>
                ) : null}
              </RealMap>
            </div>
          </div>
        </div>

        {/* Right detail panel — literal port of showProjDetail */}
        <div className="dp" id="proj-dp" style={{ display: active ? 'block' : 'none' }}>
          {active ? (
            <>
              {/* Tab bar — flat row with bottom border, per prototype showProjTabs */}
              <div style={{ display: 'flex', gap: 3, padding: '0 0 12px', borderBottom: '1px solid var(--border)', marginBottom: 12 }}>
                {([
                  { k: 'details' as const, lbl: '📋 Details' },
                  { k: 'docs' as const, lbl: `📄 Docs (${activeDocsLive.length})` },
                  { k: 'signin' as const, lbl: '✅ Sign In' },
                  { k: 'sigs' as const, lbl: '✍ Sigs' },
                ]).map((t) => (
                  <div
                    key={t.k}
                    className={`tab ${pTab === t.k ? 'active' : ''}`}
                    style={{ cursor: 'pointer', fontSize: 11 }}
                    onClick={() => setPTab(t.k)}
                  >
                    {t.lbl}
                  </div>
                ))}
              </div>

              {/* Header — code on left, Edit/× on right (same row); title + badge below */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 11 }}>{active.code}</span>
                  <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                    {canAdd ? <button className="btn btn-primary btn-sm" onClick={() => setEditing(active)}>✏ Edit</button> : null}
                    <button className="close-btn" onClick={closeDp}>×</button>
                  </div>
                </div>
                <div className="dp-title" style={{ marginTop: 3 }}>{active.name}</div>
                <div style={{ marginTop: 5 }}>{bdg(active.status)}</div>
              </div>

              {pTab === 'details' ? (
                <>
                  <div className="dp-row">
                    <span className="dp-key">Customer</span>
                    <span
                      className="dp-val"
                      style={{ cursor: active.customerRef ? 'pointer' : 'default', color: 'var(--accent)', fontWeight: 600 }}
                      onClick={() => active.customerRef && navigate(`/customers?id=${active.customerRef.id}`)}
                    >
                      {active.customerRef?.name ?? active.customer ?? '—'}{active.customerRef ? ' ↗' : ''}
                    </span>
                  </div>
                  {active.custRef || active.customerRefNo ? (
                    <div className="dp-row">
                      <span className="dp-key">Cust Ref</span>
                      <span className="dp-val">{active.custRef ?? active.customerRefNo}</span>
                    </div>
                  ) : null}
                  <div className="dp-row">
                    <span className="dp-key">Manager</span>
                    <span className="dp-val">{active.manager ?? '—'}</span>
                  </div>
                  {(() => {
                    const sup = active.supervisorId ? contacts.find((c) => c.id === active.supervisorId) : null;
                    const supName = sup?.name ?? active.supervisorName ?? null;
                    const supPhone = sup?.phone ?? active.supervisorPhone ?? null;
                    const supEmail = sup?.email ?? active.supervisorEmail ?? null;
                    if (!supName) return null;
                    return (
                      <div className="dp-row">
                        <span className="dp-key">Supervisor</span>
                        <span className="dp-val">
                          <span style={{ fontWeight: 500 }}>{supName}</span>
                          {supPhone ? <a href={`tel:${supPhone}`} style={{ color: 'var(--muted)', fontSize: 10, marginLeft: 6 }}>{supPhone}</a> : null}
                          {supEmail ? <a href={`mailto:${supEmail}`} style={{ color: 'var(--info)', fontSize: 10, marginLeft: 6 }}>{supEmail}</a> : null}
                        </span>
                      </div>
                    );
                  })()}
                  <div className="dp-row">
                    <span className="dp-key">Start</span>
                    <span className="dp-val">{active.startDate?.slice(0, 10) ?? '—'}</span>
                  </div>
                  <div className="dp-row">
                    <span className="dp-key">End</span>
                    <span className="dp-val">{active.endDate?.slice(0, 10) ?? '—'}</span>
                  </div>
                  <div className="dp-row">
                    <span className="dp-key">Address</span>
                    <span className="dp-val" style={{ color: 'var(--muted)' }}>{active.address ?? '—'}</span>
                  </div>
                  {active.postcode ? (
                    <div className="dp-row">
                      <span className="dp-key">Postcode</span>
                      <span className="dp-val">
                        <a
                          href={`https://www.google.com/maps/search/${encodeURIComponent(active.postcode)}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, textDecoration: 'none' }}
                        >📍 {active.postcode} ↗</a>
                      </span>
                    </div>
                  ) : null}

                  <div className="divider"></div>
                  <div className="sec-title">Key Project Roles</div>
                  {(() => {
                    const lookup = (id: string | null | undefined) => id ? contacts.find((c) => c.id === id) : null;
                    const pm = lookup(active.clientPMId);
                    const fm = lookup(active.clientFMId);
                    const cm = lookup(active.clientCMId);
                    const cls = lookup(active.clsMgrId);
                    return [
                      { label: 'Client PM', sub: 'Client Project Manager', id: active.clientPMId ?? null, name: pm?.name ?? null, phone: pm?.phone ?? null, email: pm?.email ?? null },
                      { label: 'Client FM', sub: 'Client Financial Manager', id: active.clientFMId ?? null, name: fm?.name ?? null, phone: fm?.phone ?? null, email: fm?.email ?? null },
                      { label: 'Client CM', sub: 'Client Construction Manager', id: active.clientCMId ?? null, name: cm?.name ?? null, phone: cm?.phone ?? null, email: cm?.email ?? null },
                      { label: 'CLS Manager', sub: 'CLS Account Manager', id: active.clsMgrId ?? null, name: cls?.name ?? null, phone: cls?.phone ?? null, email: cls?.email ?? null },
                    ];
                  })().map((r) => {
                    const waMsg = `Hi ${r.name ? r.name.split(' ')[0] : ''}, re: ${active.code} ${active.name}`;
                    return (
                      <div key={r.label} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 6, background: 'var(--card)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: r.name ? 4 : 0 }}>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', fontFamily: 'Syne, sans-serif', letterSpacing: '.5px' }}>{r.label}</div>
                            <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 1 }}>{r.sub}</div>
                          </div>
                          {r.name ? (
                            <div onClick={() => r.id && navigate(`/contacts?id=${r.id}`)} style={{ flex: 1, textAlign: 'right', cursor: r.id ? 'pointer' : 'default' }}>
                              <div style={{ fontSize: 12, fontWeight: 600 }}>{r.name}</div>
                              {r.phone ? <div style={{ fontSize: 10, color: 'var(--muted)' }}>{r.phone}</div> : null}
                              {r.email ? <div style={{ fontSize: 10, color: 'var(--info)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>{r.email}</div> : null}
                            </div>
                          ) : (
                            <div style={{ fontSize: 10, color: 'var(--faint)', fontStyle: 'italic' }}>— Not assigned —</div>
                          )}
                        </div>
                        {r.name ? (
                          <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                            {r.phone ? <a href={waLink(r.phone, waMsg)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="btn btn-wa btn-xs" title={`WhatsApp ${r.name}`}><WaIcon size={11} /> WA</a> : null}
                            {r.email ? <a href={`mailto:${r.email}?subject=${encodeURIComponent(`${active.code} ${active.name}`)}`} onClick={(e) => e.stopPropagation()} className="btn btn-info btn-xs" title={`Email ${r.name}`}>✉ Email</a> : null}
                            {r.phone ? <a href={`sms:${r.phone.replace(/\s+/g, '')}`} onClick={(e) => e.stopPropagation()} className="btn btn-ghost btn-xs" title={`SMS ${r.name}`}>📱 SMS</a> : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}

                  <div className="divider"></div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0' }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>📄 Documents <span style={{ color: 'var(--muted)', fontWeight: 400 }}>({activeDocsLive.length} active)</span></div>
                    <button className="btn btn-ghost btn-xs" onClick={() => setPTab('docs')}>View all →</button>
                  </div>

                  <div className="divider"></div>
                  <div className="sec-title">Workers on Site ({activeWorkers.length})</div>
                  {activeWorkers.length === 0 ? (
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>None assigned</div>
                  ) : activeWorkers.map((w) => (
                    <div
                      key={w.id}
                      onClick={() => navigate(`/workers?id=${w.id}`)}
                      style={{ display: 'flex', gap: 7, alignItems: 'center', padding: 7, borderRadius: 8, border: '1px solid var(--border)', marginBottom: 5, cursor: 'pointer', background: 'var(--card)', transition: 'border-color .15s' }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
                    >
                      {avEl(w, 26, 10)}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 500 }}>{w.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--muted)' }}>{(w.roles && w.roles.length ? w.roles : [w.role]).join(', ')}</div>
                      </div>
                      <span className={`badge b-${w.status}`}>{w.status === 'dnu' ? 'DNU' : w.status}</span>
                      {w.phone ? (
                        <a
                          href={waLink(w.phone, `Hi ${w.name.split(' ')[0]}, checking in re: ${active.code}`)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="btn btn-wa btn-xs"
                        ><WaIcon size={11} /></a>
                      ) : null}
                    </div>
                  ))}

                  <div className="divider"></div>
                  <div className="sec-title">Recent Deployments</div>
                  {activeRecentDeps.length === 0 ? (
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>No records</div>
                  ) : activeRecentDeps.map((r) => (
                    <div
                      key={r.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0', borderBottom: '1px solid rgba(30,42,64,.4)', fontSize: 10, cursor: 'pointer' }}
                      onClick={() => navigate(`/deploylog?id=${r.id}`)}
                    >
                      <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, color: 'var(--faint)', fontSize: 9 }}>{r.id.slice(0, 8)}</span>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontWeight: 500 }}>{r.workerName ?? workers.find((w) => w.id === r.workerId)?.name ?? '—'}</span>
                        <span style={{ color: 'var(--muted)', marginLeft: 6 }}>{r.startDate.slice(0, 10)}</span>
                      </div>
                      <span className={`badge b-${r.status === 'active' ? 'active' : 'dep-done'}`} style={{ fontSize: 8 }}>{r.status}</span>
                    </div>
                  ))}
                  {activeRecentDeps.length > 0 ? (
                    <button className="btn btn-ghost btn-sm" style={{ marginTop: 6 }} onClick={() => navigate(`/deploylog?projectId=${active.id}`)}>All records →</button>
                  ) : null}

                  <div className="divider"></div>
                  <div className="sec-title">Contacts</div>
                  {activeContacts.length === 0 ? (
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>None</div>
                  ) : activeContacts.map((ct) => (
                    <div
                      key={ct.id}
                      onClick={() => navigate(`/contacts?id=${ct.id}`)}
                      style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 7, borderRadius: 8, border: '1px solid var(--border)', marginBottom: 5, cursor: 'pointer', background: 'var(--card)', transition: 'border-color .15s' }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
                    >
                      {avEl({ name: ct.name }, 26, 10)}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 500 }}>{ct.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--muted)' }}>{ct.role ?? ''}</div>
                        {ct.email ? <a href={`mailto:${ct.email}`} onClick={(e) => e.stopPropagation()} style={{ fontSize: 10, color: 'var(--info)', textDecoration: 'none' }}>{ct.email}</a> : null}
                      </div>
                    </div>
                  ))}
                </>
              ) : null}

              {pTab === 'docs' ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 12, fontWeight: 700 }}>📄 Documents
                      <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400, marginLeft: 6 }}>
                        {activeDocsLive.length} active{activeDocsVoided.length ? ` · ${activeDocsVoided.length} voided` : ''}
                      </span>
                    </div>
                    {canAdd ? <button className="btn btn-primary btn-sm" onClick={() => setDocAddFor(active)}>+ Add Record</button> : null}
                  </div>
                  {activeDocsLive.length === 0 ? (
                    <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 12, padding: 20, border: '2px dashed var(--border)', borderRadius: 9 }}>
                      No documents yet — click "+ Add Record" to upload
                    </div>
                  ) : activeDocsLive.map((d) => (
                    <div
                      key={d.id}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 8px', borderRadius: 9, border: '1px solid var(--border)', marginBottom: 6, background: 'var(--card)' }}
                    >
                      <div style={{ fontSize: 20, lineHeight: 1 }}>{(d.type && DOC_ICONS[d.type]) ?? '📄'}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 1 }}>{d.type ?? 'Other'}{d.ref ? ` · ${d.ref}` : ''}{d.date ? ` · ${d.date.slice(0, 10)}` : ''}</div>
                        {d.notes ? <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 2 }}>{d.notes}</div> : null}
                      </div>
                      <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                        {d.url ? (
                          <button className="btn btn-ghost btn-xs" onClick={() => setDocViewer({ url: d.url!, name: d.name })} title="View">👁</button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                  {activeDocsVoided.length > 0 ? (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 10, color: 'var(--danger)', letterSpacing: 1, textTransform: 'uppercase', fontFamily: 'Syne, sans-serif', fontWeight: 700, marginBottom: 7, paddingTop: 10, borderTop: '1px solid rgba(239,68,68,.2)' }}>
                        ⊘ Voided Records ({activeDocsVoided.length}) — kept for audit
                      </div>
                      {activeDocsVoided.map((d) => (
                        <div
                          key={d.id}
                          style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 8px', borderRadius: 9, border: '1px solid rgba(239,68,68,.2)', marginBottom: 6, background: 'rgba(239,68,68,.04)', opacity: 0.75 }}
                        >
                          <div style={{ fontSize: 20, lineHeight: 1 }}>{(d.type && DOC_ICONS[d.type]) ?? '📄'}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--danger)', textDecoration: 'line-through' }}>{d.name}</div>
                            <div style={{ fontSize: 9, color: 'var(--danger)', marginTop: 3 }}>⊘ Voided{d.removedAt ? ` ${new Date(d.removedAt).toLocaleDateString('en-GB')}` : ''}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : null}

              {pTab === 'signin' ? (
                <>
                  <div style={{ display: 'flex', gap: 9, marginBottom: 12 }}>
                    <div style={{ background: 'var(--live-dim)', border: '1px solid rgba(34,197,94,.25)', borderRadius: 8, padding: '7px 12px', textAlign: 'center', flex: 1 }}>
                      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 18, color: 'var(--live)' }}>{projSignins.filter((r) => r.type === 'in').length}</div>
                      <div style={{ fontSize: 9, color: 'var(--live)', textTransform: 'uppercase' }}>Ins</div>
                    </div>
                    <div style={{ background: 'var(--done-dim)', border: '1px solid rgba(107,122,153,.25)', borderRadius: 8, padding: '7px 12px', textAlign: 'center', flex: 1 }}>
                      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 18, color: 'var(--done)' }}>{projSignins.filter((r) => r.type === 'out').length}</div>
                      <div style={{ fontSize: 9, color: 'var(--done)', textTransform: 'uppercase' }}>Outs</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 9 }}>
                    <div className="tbl-search" style={{ flex: 1 }}>
                      <span style={{ color: 'var(--faint)' }}>⌕</span>
                      <input placeholder="Search…" value={siSearch} onChange={(e) => setSiSearch(e.target.value)} />
                    </div>
                    <select className="form-select" value={siType} onChange={(e) => setSiType(e.target.value)} style={{ fontSize: 10, padding: '4px 7px' }}>
                      <option value="">All</option>
                      <option value="in">IN</option>
                      <option value="out">OUT</option>
                    </select>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ fontSize: 11, margin: 0 }}>
                      <thead>
                        <tr><th>Worker</th><th>Type</th><th>Date</th><th>Time</th><th>GPS</th></tr>
                      </thead>
                      <tbody>
                        {filteredSignins.length === 0 ? (
                          <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--muted)', padding: 20 }}>No records for this project</td></tr>
                        ) : filteredSignins.map((r) => (
                          <tr key={r.id}>
                            <td style={{ cursor: 'pointer' }} onClick={() => navigate(`/workers?id=${r.workerId}`)}>
                              {r.workerName ?? workers.find((w) => w.id === r.workerId)?.name ?? '—'}
                              <div style={{ fontSize: 9, color: 'var(--muted)' }}>{r.workerRole ?? ''}</div>
                            </td>
                            <td>
                              <span className={`badge b-${r.type === 'in' ? 'active' : 'dep-done'}`} style={{ fontSize: 9 }}>
                                {r.type === 'in' ? '✅' : '🚪'} {r.type.toUpperCase()}
                              </span>
                            </td>
                            <td>{new Date(r.timestamp).toLocaleDateString('en-GB')}</td>
                            <td>{new Date(r.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</td>
                            <td style={{ textAlign: 'center' }}>
                              {r.withinGeofence === true ? <span className="geo-ok">✓</span> :
                                r.withinGeofence === false ? <span className="geo-warn">⚠</span> :
                                  <span style={{ color: 'var(--faint)' }}>—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}

              {pTab === 'sigs' ? (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    <div style={{ background: 'var(--live-dim)', border: '1px solid rgba(34,197,94,.25)', borderRadius: 8, padding: '7px 14px', textAlign: 'center', flex: 1 }}>
                      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 20, color: 'var(--live)' }}>{sigsSigned}</div>
                      <div style={{ fontSize: 9, color: 'var(--live)', textTransform: 'uppercase', letterSpacing: 1 }}>Signed</div>
                    </div>
                    <div style={{ background: 'var(--warn-dim)', border: '1px solid rgba(245,158,11,.25)', borderRadius: 8, padding: '7px 14px', textAlign: 'center', flex: 1 }}>
                      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 20, color: 'var(--warn)' }}>{sigsPending}</div>
                      <div style={{ fontSize: 9, color: 'var(--warn)', textTransform: 'uppercase', letterSpacing: 1 }}>Pending</div>
                    </div>
                    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 14px', textAlign: 'center', flex: 1 }}>
                      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 20 }}>{projSigs.length}</div>
                      <div style={{ fontSize: 9, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Total</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                    <div className="tbl-search" style={{ flex: 1, minWidth: 120 }}>
                      <span style={{ color: 'var(--faint)' }}>⌕</span>
                      <input placeholder="Search…" value={sigSearch} onChange={(e) => setSigSearch(e.target.value)} style={{ fontSize: 11 }} />
                    </div>
                    <select className="form-select" value={sigFilter} onChange={(e) => setSigFilter(e.target.value)} style={{ fontSize: 11, padding: '5px 8px' }}>
                      <option value="">All</option>
                      <option value="signed">Signed</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  <div style={{ overflowX: 'auto', maxHeight: 380, overflowY: 'auto' }}>
                    <table style={{ margin: 0, fontSize: 11 }}>
                      <thead style={{ position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 2 }}>
                        <tr><th>Document</th><th>Worker</th><th>Status</th><th>Date</th><th>Time</th><th>Sig</th></tr>
                      </thead>
                      <tbody>
                        {filteredSigs.length === 0 ? (
                          <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--muted)', padding: 20 }}>No documents shared for this project yet</td></tr>
                        ) : filteredSigs.map((s) => (
                          <tr key={`${s.sdId}-${s.rcpId}`}>
                            <td style={{ fontSize: 11, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.docName}>{s.docName}</td>
                            <td style={{ cursor: s.workerId ? 'pointer' : 'default' }} onClick={() => { if (s.workerId) navigate(`/workers?id=${s.workerId}`); }}>
                              {s.workerName}
                              <div style={{ fontSize: 9, color: 'var(--muted)' }}>{s.workerRole}</div>
                            </td>
                            <td>
                              {s.signed
                                ? <span className="sign-chip-signed">✓ SIGNED</span>
                                : <span className="sign-chip-pending">⏱ PENDING</span>}
                            </td>
                            <td style={{ fontSize: 10, whiteSpace: 'nowrap' }}>{s.signedAt ? new Date(s.signedAt).toLocaleDateString('en-GB') : s.signed ? '—' : 'Awaiting'}</td>
                            <td style={{ fontSize: 10, whiteSpace: 'nowrap' }}>{s.signedAt ? new Date(s.signedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</td>
                            <td style={{ textAlign: 'center' }}>
                              {s.sigData ? <img src={s.sigData} style={{ height: 28, width: 'auto', background: '#fff', borderRadius: 3, padding: 2 }} alt={s.nameConfirmed} title={s.nameConfirmed} /> : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
      {addOpen ? <AddProjectModal onClose={() => setAddOpen(false)} /> : null}
      {editing ? <AddProjectModal onClose={() => setEditing(null)} initial={{ ...editing, customerId: editing.customerRef?.id ?? null } as unknown as Parameters<typeof AddProjectModal>[0]['initial']} /> : null}
      {importOpen ? <UploadModal entity="projects" onClose={() => setImportOpen(false)} /> : null}
      {docAddFor ? <AddDocModal onClose={() => setDocAddFor(null)} projectId={docAddFor.id} projectName={docAddFor.name} /> : null}
      {docViewer ? <DocViewerModal onClose={() => setDocViewer(null)} url={docViewer.url} name={docViewer.name} /> : null}
    </div>
  );
}
